import nodejsCrypto from 'crypto';
import { isServer } from '../../../utils/utils';
import { AssetType } from './types';
import type { Product } from '../../../bff-api/products/model';
import type { AssetInfo, BuildAssetsOptions } from './types';

const YOUTUBE_EMBED_BASE_URL = 'https://www.youtube.com/embed/';

async function getHash(toHash: string): Promise<string> {
    if (isServer) {
        const hash = nodejsCrypto.createHash('sha1');
        const data = hash.update(toHash, 'utf-8');
        return data.digest('hex');
    }
    return Array.from(
        new Uint8Array(await crypto.subtle.digest('SHA-1', new TextEncoder().encode(toHash))),
        (byte) => byte.toString(16).padStart(2, '0'),
    ).join('');
}

const baseShape: Omit<AssetInfo, 'assetType'> = {
    id: '',
    previewUrl: '',
    title: '',
    alt: '',
    assetUrl: '',
};

/**
 * Organizes the assets in the order to be shown
 */
function orderAssets(
    productImages: AssetInfo[],
    youTubeVideos: AssetInfo[],
    brandSpecialistVideos: AssetInfo[],
): AssetInfo[] {
    let result: AssetInfo[] = productImages.map(
        (pi): AssetInfo => ({
            ...pi,
            assetType: AssetType.IMAGE,
        }),
    );

    // Brand specialist video has to be in the second position
    if (brandSpecialistVideos.length) {
        result.splice(1, 0, {
            ...brandSpecialistVideos[0],
            assetType: AssetType.BRAND_SPECIALIST_VIDEO,
        });
    }

    // If there is a Youtube video and there are more than 6 assets,
    // the video should always be shown in the sixth position
    if (result.length > 5 && youTubeVideos.length) {
        const [firstYtVideo] = youTubeVideos.splice(0, 1);
        result.splice(5, 0, {
            ...firstYtVideo,
            assetType: AssetType.YOUTUBE,
        });
    }

    if (youTubeVideos.length) {
        result = [
            ...result,
            ...youTubeVideos.map((yv): AssetInfo => ({ ...yv, assetType: AssetType.YOUTUBE })),
        ];
    }

    return result;
}

/**
 * Gets product images urls
 */
async function getProductImageUrls(product: Product): Promise<AssetInfo[]> {
    const images = product.imageGroups
        ?.filter((ig) => ig.viewType === 'large')
        .flatMap((ig) => ig.images);

    if (images?.length) {
        return Promise.all(
            images.map(async ({ link, title = '', alt = '' }) => ({
                ...baseShape,
                id: await getHash(link),
                assetUrl: link,
                title,
                alt,
                assetType: AssetType.IMAGE,
            })),
        );
    }
    return [];
}

/**
 * Gets product youtube video urls
 */
async function getYoutubeUrls(product: Product): Promise<AssetInfo[]> {
    if (product.c_youtubeVideoIds) {
        return Promise.all(
            product.c_youtubeVideoIds.split(',').map(async (ytVideoId) => ({
                ...baseShape,
                id: await getHash(ytVideoId),
                previewUrl: `https://img.youtube.com/vi/${ytVideoId}/maxresdefault.jpg`,
                assetUrl: `${YOUTUBE_EMBED_BASE_URL}${ytVideoId}`,
                assetType: AssetType.YOUTUBE,
                alt: product.name ?? '',
            })),
        );
    }
    return [];
}

/**
 * Gets product brand specialist video urls
 */
async function getBrandSpecialistVideoUrls(product: Product): Promise<AssetInfo[]> {
    if (product.c_brandSpecialistVideoUrls) {
        return Promise.all(
            product.c_brandSpecialistVideoUrls.map(
                async (brandSpecialistVideoUrl): Promise<AssetInfo> => {
                    const videoUrlParts = brandSpecialistVideoUrl.split('/');
                    if (!videoUrlParts.length) {
                        return {
                            ...baseShape,
                            id: await getHash(brandSpecialistVideoUrl),
                            previewUrl: brandSpecialistVideoUrl,
                            assetUrl: brandSpecialistVideoUrl,
                            assetType: AssetType.BRAND_SPECIALIST_VIDEO,
                            alt: product.name ?? '',
                        };
                    }
                    const videoPath = videoUrlParts[videoUrlParts.length - 1];
                    const videoPathParts = videoPath.split('-');
                    if (!videoPathParts.length) {
                        return {
                            ...baseShape,
                            id: await getHash(brandSpecialistVideoUrl),
                            previewUrl: brandSpecialistVideoUrl,
                            assetUrl: brandSpecialistVideoUrl,
                            assetType: AssetType.BRAND_SPECIALIST_VIDEO,
                            alt: product.name ?? '',
                        };
                    }
                    videoPathParts.splice(0, 1, 'webimage');

                    const thumbnailUrl = `${brandSpecialistVideoUrl.substring(
                        0,
                        brandSpecialistVideoUrl.lastIndexOf('/'),
                    )}/${videoPathParts.join('-')}`;

                    return {
                        ...baseShape,
                        id: await getHash(brandSpecialistVideoUrl),
                        previewUrl: thumbnailUrl,
                        assetUrl: brandSpecialistVideoUrl,
                        assetType: AssetType.BRAND_SPECIALIST_VIDEO,
                        alt: product.name ?? '',
                    };
                },
            ),
        );
    }
    return [];
}

/**
 * Gets product images for photo grid
 */
export async function buildAssets(
    product: Product,
    options: BuildAssetsOptions,
): Promise<AssetInfo[]> {
    const productImages = await getProductImageUrls(product);
    const youtubeVideos = await getYoutubeUrls(product);
    let brandSpecialistVideos: AssetInfo[] = [];

    if (options.isBrandSpecialistVideosEnabled) {
        brandSpecialistVideos = await getBrandSpecialistVideoUrls(product);
    }

    return orderAssets(productImages, youtubeVideos, brandSpecialistVideos);
}
